import {
  MemberStatusChangeRequestAuditForm,
  MemberStatusChangeRequestDto,
  MemberStatusChangeRequestForm,
  MemberStatusDto,
} from '@act/shared/data-transfer-objects';
import { SelectOption } from '@act/storybook';
import InfoIcon from '@mui/icons-material/Info';
import { Alert, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import HtmlDisplayer from 'react-components/components/HtmlDisplayer';
import {
  FormConfig,
  FormItem,
  FormPassthrough,
} from 'stories/forms/FormBuilder/formBuilder.types';
import styled from 'styled-components';
import { reemaColors } from 'toolkit/styles/colors';
import {
  canApproveStatusChangeRequest,
  getStatusChangeRequestDateRestrictionDescription,
  isInGracePeriodForStatusChangeRequest,
} from 'utilities/date-validation/statusChangeRequestDates.util';

const SelectedOptionDisplay = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
  ul {
    margin: 0;
    padding-left: 14px;
  }
`;

interface FormConfigParams {
  isAuditingRequest: boolean;
  selectOptions: SelectOption<number>[];
  selectedOption: MemberStatusDto;
  minDate: DateTime;
  member: any;
  author: any;
  memberStatusChangeRequest: MemberStatusChangeRequestDto;
  currentEffectiveAt: string;
}

export const createMemberStatusChangeRequestFormConfig = ({
  isAuditingRequest,
  selectOptions,
  selectedOption,
  minDate,
  member,
  author,
  memberStatusChangeRequest,
  currentEffectiveAt,
}: FormConfigParams): FormConfig<
  MemberStatusChangeRequestForm | MemberStatusChangeRequestAuditForm
> => {
  return [
    // Request form fields (shown when not auditing)
    ...(!isAuditingRequest
      ? [
          {
            type: 'input',
            name: 'memberStatusId',
            label: 'What is the reason for change of status?',
            input: 'select',
            props: {
              options: selectOptions,
              required: true,
              fullWidth: true,
              trackingName: 'member-status-select',
            },
          } as FormItem<MemberStatusChangeRequestForm>,
          // Combined info alert for status requirements and date restrictions
          (selectedOption && selectedOption.changeRequestDescription) ||
          isInGracePeriodForStatusChangeRequest()
            ? ({
                type: 'passthrough',
                content: (
                  <Alert
                    severity="info"
                    sx={{
                      backgroundColor: reemaColors.removedBackground,
                      marginBottom: '12px',
                    }}
                    icon={<InfoIcon fontSize="inherit" />}
                  >
                    <div>
                      {selectedOption &&
                        selectedOption.changeRequestDescription && (
                          <SelectedOptionDisplay>
                            <h6>{selectedOption.name} status requirement</h6>
                            <HtmlDisplayer
                              className="description"
                              innerHtml={
                                selectedOption.changeRequestDescription
                              }
                            />
                          </SelectedOptionDisplay>
                        )}
                      {isInGracePeriodForStatusChangeRequest() && (
                        <>
                          {selectedOption &&
                            selectedOption.changeRequestDescription && (
                              <hr
                                style={{
                                  margin: '8px 0',
                                  border: 'none',
                                  borderTop: '1px solid rgba(0,0,0,0.12)',
                                }}
                              />
                            )}
                          <Typography variant="body2">
                            {getStatusChangeRequestDateRestrictionDescription()}
                          </Typography>
                        </>
                      )}
                    </div>
                  </Alert>
                ),
              } as FormPassthrough)
            : null,
          selectedOption &&
            ({
              type: 'input',
              name: 'effectiveAt',
              label:
                selectedOption?.effectiveAtQuestionText ||
                'When should this status change occur?',
              input: 'date',
              props: {
                required: true,
                minDate: minDate,
                trackingName: 'effective-at-date-input',
              },
            } as FormItem<MemberStatusChangeRequestForm>),
          selectedOption &&
            ({
              type: 'input',
              name: 'creatorComment',
              label: 'Leave a comment to help the team process your request.',
              input: 'text',
              props: {
                maxLength: 500,
                multiline: true,
                rows: 3,
                trackingName: 'creator-comment-input',
              },
            } as FormItem<MemberStatusChangeRequestForm>),
        ]
      : []),
    // Audit form fields (shown when auditing)
    ...(isAuditingRequest
      ? [
          {
            type: 'passthrough',
            content: (
              <div className="info-pane">
                <h6>Member</h6>
                <p>
                  {member.firstName} {member.lastName}
                </p>
                {selectedOption && (
                  <>
                    <h6>Reason for Change Request</h6>
                    <p>{selectedOption.name}</p>
                  </>
                )}
                <h6>Requesting Guide</h6>
                <p>{author?.name}</p>
                <h6>Comments</h6>
                {memberStatusChangeRequest.creatorComment}
              </div>
            ),
          } as FormPassthrough,
          // Info alert about date restrictions for auditing (only show when in grace period)
          isInGracePeriodForStatusChangeRequest() &&
            ({
              type: 'passthrough',
              content: (
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor: reemaColors.removedBackground,
                    marginBottom: '12px',
                  }}
                  icon={<InfoIcon fontSize="inherit" />}
                >
                  <Typography variant="body2">
                    {getStatusChangeRequestDateRestrictionDescription()}
                  </Typography>
                </Alert>
              ),
            } as FormPassthrough),
          // Warning alert if request can no longer be approved due to grace period expiration
          // Show this warning whenever auditing a request that can't be approved
          !canApproveStatusChangeRequest(
            currentEffectiveAt || memberStatusChangeRequest.effectiveAt
          ) &&
            ({
              type: 'passthrough',
              content: (
                <Alert
                  severity="warning"
                  sx={{
                    marginBottom: '12px',
                  }}
                  icon={<InfoIcon fontSize="inherit" />}
                >
                  <Typography variant="body2">
                    This request is for a date that is no longer allowed for
                    status change requests. The grace period for previous month
                    changes has elapsed.
                  </Typography>
                </Alert>
              ),
            } as FormPassthrough),
          {
            type: 'input',
            name: 'effectiveAt',
            label:
              selectedOption?.effectiveAtQuestionText ||
              'When should this status change occur?',
            input: 'date',
            props: {
              required: true,
              minDate: minDate,
            },
          } as FormItem<MemberStatusChangeRequestAuditForm>,
          {
            type: 'input',
            name: 'approval',
            label: 'Member status change approval/denial',
            input: 'radioGroup',
            props: {
              options: [
                {
                  label: 'Approved',
                  value: 'approve',
                  disabled: !canApproveStatusChangeRequest(
                    currentEffectiveAt || memberStatusChangeRequest.effectiveAt
                  ),
                },
                { label: 'Denied', value: 'deny' },
              ],
              trackingName: 'approval-radio-group',
            },
          } as FormItem<MemberStatusChangeRequestAuditForm>,
          {
            type: 'input',
            name: 'auditorComment',
            label: 'Explain the reason(s) for the decision.',
            input: 'text',
            props: {
              maxLength: 500,
              multiline: true,
              rows: 3,
              trackingName: 'auditor-comment-input',
            },
          } as FormItem<MemberStatusChangeRequestAuditForm>,
        ]
      : []),
  ].filter(Boolean);
};
